/**
 * Monta a proposta comercial.
 *
 * O total recalcula a cada tecla, na própria tela (`calcularTotais`, a mesma conta do servidor):
 * quem negocia mexe nas vidas com o cliente ao telefone, e um total que só aparece depois de
 * salvar não serve para essa conversa.
 *
 * As duas chaves que mudam o documento impresso, e não só o cálculo:
 *  - **Mostrar valor por vida** — base grande fica melhor com o unitário à vista; base pequena
 *    fica melhor só com o total.
 *  - **ASO incluso** — quando desligado, o documento imprime a cláusula dizendo que exames e ASO
 *    são cobrados à parte. É o que evita a discussão na primeira fatura.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Print as PrintIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { PageShell } from '../../components';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { FalhaDeCarga, falhaDeCarga, textoDaFalhaDeCarga } from '../../utils/acessoNegado';
import proposalService from '../../services/proposalService';
import consultantService from '../../services/consultantService';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import type { Consultor } from '../../types/consultant';
import {
  calcularTotais,
  emReais,
  ROTULO_DA_SITUACAO,
  SITUACAO_DA_PROPOSTA,
  type DadosDaProposta,
  type SituacaoDaProposta,
} from '../../types/proposal';

/** Os valores de tabela hoje. Ficam aqui só como ponto de partida: a proposta guarda os dela. */
const PADRAO: DadosDaProposta = {
  clientName: '',
  clientCnpj: null,
  clientContact: null,
  lives: 0,
  pricePerLife: 24,
  showPricePerLife: true,
  includesAso: false,
  managementEnabled: true,
  clubEnabled: false,
  clubBaseFee: 800,
  clubBaseLives: 100,
  clubExtraPerLife: 1.8,
  contractMonths: 12,
  validUntil: null,
  readjustmentNote: 'Reajuste anual pelo IPCA.',
  consultantName: null,
  consultantPhone: null,
  consultantEmail: null,
  status: 'RASCUNHO',
  notes: null,
  consultantId: null,
  modules: [],
};

/** Campo numérico: string vazia vira 0, e não NaN — NaN contamina todo o total. */
function paraNumero(valor: string): number {
  const n = Number(valor.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/**
 * O que a pessoa digitou, com vazio virando nulo.
 *
 * **Não apara espaços.** Aparar aqui parece inofensivo e quebra o campo inteiro: o `onChange`
 * roda a cada tecla, então digitar "Paulo" e apertar espaço devolveria "Paulo" ao input
 * controlado — e a pessoa nunca conseguiria escrever a segunda palavra. Quem apara é o servidor,
 * na hora de gravar, que é quando aparar significa alguma coisa.
 */
function comoDigitado(valor: string): string | null {
  return valor === '' ? null : valor;
}

export const ProposalFormPage: React.FC = () => {
  const navegar = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editando = id !== undefined && id !== 'nova';

  const [dados, setDados] = useState<DadosDaProposta>(PADRAO);
  const [numero, setNumero] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(editando);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);

  useEffect(() => {
    if (!editando || id === undefined) return;
    let ativo = true;
    void (async () => {
      try {
        const p = await proposalService.porId(Number(id));
        if (!ativo) return;
        setNumero(p.number);
        setDados({
          clientName: p.clientName,
          clientCnpj: p.clientCnpj,
          clientContact: p.clientContact,
          lives: Number(p.lives),
          pricePerLife: Number(p.pricePerLife),
          showPricePerLife: Boolean(p.showPricePerLife),
          includesAso: Boolean(p.includesAso),
          managementEnabled: p.managementEnabled !== false,
          clubEnabled: Boolean(p.clubEnabled),
          clubBaseFee: Number(p.clubBaseFee),
          clubBaseLives: Number(p.clubBaseLives),
          clubExtraPerLife: Number(p.clubExtraPerLife),
          contractMonths: Number(p.contractMonths),
          validUntil: p.validUntil,
          readjustmentNote: p.readjustmentNote,
          consultantName: p.consultantName,
          consultantPhone: p.consultantPhone,
          consultantEmail: p.consultantEmail,
          status: p.status,
          notes: p.notes,
          consultantId: p.consultantId,
          modules: p.modules ?? [],
        });
      } catch (err: unknown) {
        if (ativo) {
          setFalha(falhaDeCarga(err, 'Não foi possível carregar a proposta. Tente de novo.'));
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [editando, id]);

  const mudar = useCallback(<C extends keyof DadosDaProposta>(campo: C, valor: DadosDaProposta[C]) => {
    setDados((anterior) => ({ ...anterior, [campo]: valor }));
  }, []);

  /**
   * A equipe comercial.
   *
   * Vem por `useListaPermitida` e não por chamada direta: a porta desta tela cobra
   * `proposal.create`, e a lista de consultores cobra `consultant.read`. Quem tem uma e não a
   * outra abriria o formulário com o campo vazio, sem saber por quê — o gancho pergunta antes de
   * pedir e devolve o motivo em `bloqueio`.
   *
   * Só os ativos: quem saiu continua nas propostas antigas (o nome está copiado nelas), mas não
   * deve ser oferecido para assinar uma nova.
   */
  const carregarEquipe = useCallback(
    async () => (await consultantService.listar()).filter((c) => c.isActive),
    []
  );
  const equipe = useListaPermitida<Consultor>(
    'consultant.read',
    'Consultores',
    carregarEquipe
  );
  const consultores = equipe.itens;

  /**
   * Equipe de um: escolher é óbvio, e fazer a pessoa escolher o único item da lista toda vez é
   * exatamente o trabalho que este cadastro veio tirar. Só em proposta nova — sobrescrever quem
   * já assinou uma proposta gravada seria trocar o autor dela.
   *
   * O `jaSugeriu` faz isso acontecer **uma vez só**. Sem ele, o efeito reaplica a sugestão sempre
   * que suas dependências mudam, e quem escolheu "Digitar à mão" — deixando `consultantId` nulo —
   * veria o consultor voltar sozinho. Preenchimento automático pode adiantar uma escolha; não
   * pode desfazer a escolha de uma pessoa.
   */
  const jaSugeriu = useRef(false);
  useEffect(() => {
    if (jaSugeriu.current || editando || !equipe.resolvida || consultores.length !== 1) return;
    const unico = consultores[0];
    if (!unico) return;
    jaSugeriu.current = true;
    setDados((anterior) =>
      anterior.consultantId
        ? anterior
        : {
            ...anterior,
            consultantId: unico.id,
            consultantName: unico.name,
            consultantPhone: unico.phone,
            consultantEmail: unico.email,
          }
    );
  }, [consultores, editando, equipe.resolvida]);

  /**
   * Escolher o consultor **copia** os dados para a proposta, em vez de só guardar a referência.
   * É a mesma regra do preço: se o telefone dele mudar em março, a proposta enviada em janeiro
   * tem de continuar mostrando o telefone que o cliente recebeu.
   */
  const escolherConsultor = useCallback(
    (id: string) => {
      // Compara como texto, e nao como numero: as colunas de id sao BIGINT e o driver roda com
      // `bigNumberStrings`, entao `c.id` chega "1" (texto) enquanto o `<select>` devolve "1"
      // tambem. Converter um dos lados para numero fazia `"1" === 1` dar falso, e escolher o
      // consultor de volta limpava os tres campos em vez de preenche-los.
      const escolhido = consultores.find((c) => String(c.id) === id);
      setDados((anterior) => ({
        ...anterior,
        consultantId: escolhido?.id ?? null,
        consultantName: escolhido?.name ?? null,
        consultantPhone: escolhido?.phone ?? null,
        consultantEmail: escolhido?.email ?? null,
      }));
    },
    [consultores]
  );

  /**
   * Os módulos são texto livre, escritos na hora da negociação. Não vêm de catálogo de propósito:
   * o que se vende muda mais rápido do que um cadastro acompanharia, e a proposta guarda cópia.
   */
  const adicionarModulo = useCallback(() => {
    setDados((anterior) => ({
      ...anterior,
      modules: [...anterior.modules, { name: '', description: null }],
    }));
  }, []);

  const mudarModulo = useCallback(
    (indice: number, campo: 'name' | 'description', valor: string) => {
      setDados((anterior) => ({
        ...anterior,
        modules: anterior.modules.map((m, i) =>
          i === indice ? { ...m, [campo]: campo === 'description' ? comoDigitado(valor) : valor } : m
        ),
      }));
    },
    []
  );

  const removerModulo = useCallback((indice: number) => {
    setDados((anterior) => ({
      ...anterior,
      modules: anterior.modules.filter((_, i) => i !== indice),
    }));
  }, []);

  const totais = useMemo(() => calcularTotais(dados), [dados]);

  const salvar = useCallback(async () => {
    if (dados.clientName.trim() === '') {
      setErro('O nome do cliente é obrigatório.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const salva = editando
        ? await proposalService.atualizar(Number(id), dados)
        : await proposalService.criar(dados);
      navegar(`/proposals/${salva.id}/documento`);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível salvar a proposta');
      setSalvando(false);
    }
  }, [dados, editando, id, navegar]);

  if (editando && falha) {
    return (
      <PageShell
        breadcrumbs={[
          { label: 'Início', path: '/' },
          { label: 'Propostas', path: '/proposals' },
          { label: 'Proposta' },
        ]}
        title="Proposta"
      >
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Proposta não encontrada.')}
          rotuloDoBotao="Voltar para as propostas"
          aoVoltar={() => navegar('/proposals')}
        />
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Propostas', path: '/proposals' },
        { label: editando ? (numero ?? 'Proposta') : 'Nova proposta' },
      ]}
      title={editando ? `Proposta ${numero ?? ''}` : 'Nova proposta'}
      subtitle="O total recalcula enquanto você digita"
      actions={
        <Stack direction="row" spacing={1}>
          {editando && (
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => navegar(`/proposals/${String(id)}/documento`)}
            >
              Documento
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={() => void salvar()}
            disabled={salvando || carregando}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </Button>
        </Stack>
      }
    >
      {erro && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erro}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={2}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Cliente
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 7 }}>
                    <TextField
                      fullWidth
                      required
                      label="Razão social"
                      value={dados.clientName}
                      onChange={(e) => mudar('clientName', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <TextField
                      fullWidth
                      label="CNPJ"
                      value={dados.clientCnpj ?? ''}
                      onChange={(e) => mudar('clientCnpj', comoDigitado(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="A/C (quem recebe a proposta)"
                      value={dados.clientContact ?? ''}
                      onChange={(e) => mudar('clientContact', comoDigitado(e.target.value))}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Gestão em SST
                  </Typography>
                  {/* Desligar deixa a proposta só com o Clube — a venda de NR-1. */}
                  <Switch
                    checked={dados.managementEnabled}
                    onChange={(e) => mudar('managementEnabled', e.target.checked)}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Plataforma, agendamentos, avisos de vencimento de exames e EPI, documentos
                  legais e eSocial. Cobrada por vida ativa.
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Vidas"
                      disabled={!dados.managementEnabled}
                      value={String(dados.lives)}
                      onChange={(e) => mudar('lives', Math.trunc(paraNumero(e.target.value)))}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Valor por vida (R$)"
                      disabled={!dados.managementEnabled}
                      value={String(dados.pricePerLife)}
                      onChange={(e) => mudar('pricePerLife', paraNumero(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Vigência (meses)"
                      value={String(dados.contractMonths)}
                      onChange={(e) =>
                        mudar('contractMonths', Math.trunc(paraNumero(e.target.value)))
                      }
                      helperText="Mínimo de 12 meses"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          disabled={!dados.managementEnabled}
                          checked={dados.showPricePerLife}
                          onChange={(e) => mudar('showPricePerLife', e.target.checked)}
                        />
                      }
                      label="Mostrar o valor por vida no documento"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={dados.includesAso}
                          onChange={(e) => mudar('includesAso', e.target.checked)}
                        />
                      }
                      label="Exames e ASO inclusos no plano"
                    />
                    {!dados.managementEnabled && !dados.clubEnabled && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Sem gestão e sem Clube a proposta não cobra nada. Ligue ao menos um.
                </Alert>
              )}

              {!dados.includesAso && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        O documento vai imprimir a cláusula de que exames e ASO são cobrados à
                        parte pela clínica.
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Clube Lidera
                  </Typography>
                  <Switch
                    checked={dados.clubEnabled}
                    onChange={(e) => mudar('clubEnabled', e.target.checked)}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Plugin de bem-estar: acompanhamento da saúde mental do colaborador. Valor
                  fechado até a faixa, mais o excedente por vida acima dela.
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Valor da faixa (R$)"
                      value={String(dados.clubBaseFee)}
                      disabled={!dados.clubEnabled}
                      onChange={(e) => mudar('clubBaseFee', paraNumero(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Vidas na faixa"
                      value={String(dados.clubBaseLives)}
                      disabled={!dados.clubEnabled}
                      onChange={(e) =>
                        mudar('clubBaseLives', Math.trunc(paraNumero(e.target.value)))
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Vida excedente (R$)"
                      value={String(dados.clubExtraPerLife)}
                      disabled={!dados.clubEnabled}
                      onChange={(e) => mudar('clubExtraPerLife', paraNumero(e.target.value))}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Módulos inclusos
                  </Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={adicionarModulo}>
                    Adicionar
                  </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  O que entra junto — canal de denúncia, IA para plano de ação, o que a negociação
                  incluir. Sai listado no documento, na ordem desta tela.
                </Typography>

                {dados.modules.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum módulo ainda. A proposta sai sem esta seção.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {dados.modules.map((modulo, indice) => (
                      <Stack key={indice} direction="row" spacing={1} alignItems="flex-start">
                        <TextField
                          label="Módulo"
                          sx={{ flex: '0 0 34%' }}
                          value={modulo.name}
                          onChange={(e) => mudarModulo(indice, 'name', e.target.value)}
                        />
                        <TextField
                          fullWidth
                          label="O que ele entrega"
                          value={modulo.description ?? ''}
                          onChange={(e) => mudarModulo(indice, 'description', e.target.value)}
                        />
                        <Tooltip title="Remover módulo">
                          <IconButton
                            aria-label={`Remover módulo ${indice + 1}`}
                            onClick={() => removerModulo(indice)}
                            sx={{ mt: 1 }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Condições e assinatura
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Proposta válida até"
                      slotProps={{ inputLabel: { shrink: true } }}
                      value={dados.validUntil ?? ''}
                      onChange={(e) => mudar('validUntil', comoDigitado(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <TextField
                      fullWidth
                      label="Reajuste"
                      value={dados.readjustmentNote ?? ''}
                      onChange={(e) => mudar('readjustmentNote', comoDigitado(e.target.value))}
                    />
                  </Grid>
                  {/* Escolher preenche os três campos abaixo; eles seguem editáveis, para o
                      caso de uma proposta assinada por quem não está no cadastro. */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      fullWidth
                      label="Consultor"
                      value={dados.consultantId ?? ''}
                      onChange={(e) => escolherConsultor(e.target.value)}
                      helperText={
                        equipe.bloqueio ??
                        equipe.erro ??
                        (consultores.length === 0
                          ? 'Nenhum cadastrado — cadastre em Comercial › Consultores'
                          : 'O telefone e o e-mail vêm junto')
                      }
                    >
                      <MenuItem value="">
                        <em>Digitar à mão</em>
                      </MenuItem>
                      {consultores.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Nome que assina"
                      value={dados.consultantName ?? ''}
                      onChange={(e) => mudar('consultantName', comoDigitado(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="Telefone"
                      value={dados.consultantPhone ?? ''}
                      onChange={(e) => mudar('consultantPhone', comoDigitado(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label="E-mail"
                      value={dados.consultantEmail ?? ''}
                      onChange={(e) => mudar('consultantEmail', comoDigitado(e.target.value))}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      fullWidth
                      label="Situação"
                      value={dados.status}
                      onChange={(e) => mudar('status', e.target.value as SituacaoDaProposta)}
                    >
                      {Object.values(SITUACAO_DA_PROPOSTA).map((s) => (
                        <MenuItem key={s} value={s}>
                          {ROTULO_DA_SITUACAO[s]}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      label="Observações internas (não saem no documento)"
                      value={dados.notes ?? ''}
                      onChange={(e) => mudar('notes', comoDigitado(e.target.value))}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card variant="outlined" sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Resumo
              </Typography>

              <Stack spacing={1}>
                {dados.managementEnabled && (
                  <Linha
                    rotulo={
                      dados.showPricePerLife
                        ? `Gestão — ${totais.vidas} vidas × ${emReais(totais.valorPorVida)}`
                        : `Gestão — ${totais.vidas} vidas`
                    }
                    valor={emReais(totais.gestao)}
                  />
                )}

                {dados.clubEnabled && (
                  <>
                    <Linha
                      rotulo={`Clube — até ${dados.clubBaseLives} vidas`}
                      valor={emReais(totais.clubeBase)}
                    />
                    {totais.vidasExcedentes > 0 && (
                      <Linha
                        rotulo={`Clube — ${totais.vidasExcedentes} excedentes × ${emReais(
                          dados.clubExtraPerLife
                        )}`}
                        valor={emReais(totais.clubeExcedente)}
                      />
                    )}
                  </>
                )}

                <Divider />

                <Box display="flex" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="body1" fontWeight={600}>
                    Total mensal
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {emReais(totais.total)}
                  </Typography>
                </Box>

                <Linha
                  rotulo={`Contrato de ${dados.contractMonths} meses`}
                  valor={emReais(totais.totalDoContrato)}
                />
              </Stack>

              {!dados.includesAso && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Exames e ASO não estão inclusos: são cobrados à parte pela clínica.
                </Alert>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </PageShell>
  );
};

const Linha: React.FC<{ rotulo: string; valor: string }> = ({ rotulo, valor }) => (
  <Box display="flex" justifyContent="space-between" alignItems="baseline" gap={1}>
    <Typography variant="body2" color="text.secondary">
      {rotulo}
    </Typography>
    <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {valor}
    </Typography>
  </Box>
);

export default ProposalFormPage;
